import {
  toWSDateString,
  useIsMobile,
  useModalOldContext,
  WSButton,
  WSCheckboxGroup,
  WSFiltersOld,
  WSFlexBox,
  WSFormOld,
  WSIcon,
  WSInfiniteScroll,
  WSSelectOld,
  WSSelectOption,
  WSTable,
  WSTableCell,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { BulkStatus } from "@wingspanhq/payments/dist/interfaces";
import { IDeductionResponse } from "@wingspanhq/payments/dist/interfaces/api/deductions";
import { DeductionStatus } from "@wingspanhq/payments/dist/interfaces/deductions";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import reduce from "lodash/reduce";
import startCase from "lodash/startCase";
import React, { useMemo } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useAllCollaboratorDeductions } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { useUrlQueryFilters } from "../../../utils/router";
import { getAllEntries } from "../../../utils/serviceHelper";
import {
  mapAndRevertParamsSortToTable,
  mapAndRevertTableSortToParams,
  SortDirection
} from "../../../utils/tableSortingHelpers";
import { CompletedDeductionDetails } from "../deductions/CompletedDuductionDetails";
import { DEDUCTION_INFO_MODAL } from "../deductions/modals/DeductionsInfo";
import { ScheduledDeductionDetails } from "../deductions/ScheduledDuductionDetails";
import styles from "./AllDeductions.module.scss";

import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useBulkPayablesBatchesQuery } from "../../../modules/BulkImporter/query/bulkPayable/queries";
import { BulkResource } from "../../../modules/BulkImporter/types";
import { getUploadedFilename } from "../../../modules/BulkImporter/utils/getUploadedFilename";
import { getCommonCurrencyCode } from "../../../modules/Invoicing/utils/currency";
import { useQueryAllPayeeRows } from "../../../query/search/payee/queries/useQueryAllPayeeRows";
import { paymentsService } from "../../../services/payments";
import { ButtonDownloadCsv } from "../../../shared/components/ButtonDownloadCsv";
import { mapDeductionsTableToCSV } from "./csvUtils";
import { useAddPayeeDeduction } from "./modals/AddPayeeDeduction";

type AllDeductionsDefaultFilters = {
  bulkBatchId?: string;
  status?: [DeductionStatus];
  collaboratorEmail?: string;
  sort?: {
    name?: SortDirection;
    status?: SortDirection;
    amount?: SortDirection;
  };
};

const allDeductionsDefaultFilters = {
  bulkBatchId: undefined,
  status: undefined,
  collaboratorEmail: undefined,
  sort: undefined
};

export const AllDeductions: React.FC = () => {
  useBrowserPageTitle("All Deductions");
  const { openModal } = useModalOldContext();
  const isMobile = useIsMobile();
  const history = useHistory();
  const addPayeeDeductionModal = useAddPayeeDeduction();

  const { filters, setFilters, defaultFilters } =
    useUrlQueryFilters<AllDeductionsDefaultFilters>(
      allDeductionsDefaultFilters
    );

  const queryAllPayeeRows = useQueryAllPayeeRows();
  const qDeductions = useAllCollaboratorDeductions(
    {
      memberId: filters.collaboratorEmail
        ? queryAllPayeeRows.data?.find(
            row => row.user?.email === filters.collaboratorEmail
          )?.payeeId
        : undefined,
      status: filters.status ? { in: filters.status } : undefined,
      "labels.bulkBatchId": filters.bulkBatchId,
      sort: filters.sort
    },
    {
      enabled: !!queryAllPayeeRows.data
    }
  );
  const qBulkPayableBatches = useBulkPayablesBatchesQuery();

  const currencyCode = useMemo(
    () => getCommonCurrencyCode(flatten(qDeductions.data?.pages || [])),
    [qDeductions.data]
  );

  const onNameSort = () => {
    setFilters({
      sort: { name: mapAndRevertTableSortToParams(filters.sort?.name) }
    });
  };

  const onStatusSort = () => {
    setFilters({
      sort: {
        status: mapAndRevertTableSortToParams(filters.sort?.status)
      }
    });
  };

  const onAmountSort = () => {
    setFilters({
      sort: {
        amount: mapAndRevertTableSortToParams(filters.sort?.amount)
      }
    });
  };

  const payeeOptions = useMemo((): WSSelectOption[] => {
    const payees = (queryAllPayeeRows.data || [])
      .filter(row => !!row.user)
      .map(row => {
        const name = wsName({
          user: row.user,
          member: row.member,
          payerOwnedData: row.payerOwnedData
        });

        return {
          label: name.getResolvedName(),
          value: row.user.email,
          searchText: [
            row.user.email,
            row.user.profile?.firstName,
            row.user.profile?.lastName,
            row.member.profile?.company?.doingBusinessAs,
            row.member.profile?.company?.legalBusinessName
          ]
            .filter(Boolean)
            .join(" ")
        };
      });

    return [
      {
        label: "All",
        value: "all"
      },
      ...payees
    ];
  }, [queryAllPayeeRows.data]);

  return (
    <>
      <Switch>
        <Route
          path={`/member/invoices/payables/deductions/scheduled/:deductionId`}
          component={ScheduledDeductionDetails}
          exact
        />
        <Route
          path={`/member/invoices/payables/deductions/completed/:deductionId`}
          component={CompletedDeductionDetails}
          exact
        />
      </Switch>
      <WSFlexBox
        justify={"space-between"}
        direction={isMobile ? "column" : "row"}
      >
        <WSText.Heading1 mb="XL">
          All Deductions
          <WSIcon
            ml="M"
            size="XS"
            name="info-circle"
            color="gray400"
            onClick={() => openModal(DEDUCTION_INFO_MODAL)}
          />
        </WSText.Heading1>

        <WSButton.Primary
          mb="XL"
          fullWidth={isMobile}
          onClick={() => addPayeeDeductionModal.open()}
        >
          Add deduction
        </WSButton.Primary>
      </WSFlexBox>

      <WSQueries
        queries={{ qDeductions, queryAllPayeeRows, qBulkPayableBatches }}
      >
        {({
          qDeductions,
          queryAllPayeeRows,
          qBulkPayableBatches: { data: bulkPayableBatches }
        }) => {
          const deductions = flatten(qDeductions.data.pages || []);

          const tableData: WSTableItem<IDeductionResponse>[] = deductions.map(
            deduction => {
              const { deductionId } = deduction;

              return {
                id: deductionId,
                data: deduction
              };
            }
          );

          const filteredBulkBatches = bulkPayableBatches.filter(bulkBatch =>
            [BulkStatus.Complete, BulkStatus.Failed].includes(bulkBatch.status)
          );

          return (
            <>
              <WSFlexBox alignItems="center" justify="space-between" mb="XL">
                <WSFiltersOld
                  filtersGridItemProps={{
                    span: { s: "6" }
                  }}
                  filters={filters}
                  defaultFilters={defaultFilters}
                  getCount={(defaultValues, currentValues) => {
                    const _defaultValues = omit(defaultValues, ["sort"]);
                    const _currentValues = omit(currentValues, ["sort"]);

                    return reduce<any, any>(
                      _currentValues,
                      (result, value, key) =>
                        isEqual(value, _defaultValues[key])
                          ? result
                          : result.concat(key),
                      []
                    ).length;
                  }}
                  onFilter={newFilters => {
                    setFilters(newFilters);
                  }}
                >
                  <WSFormOld.Field
                    name="status"
                    label="Status"
                    mb="2XL"
                    component={WSCheckboxGroup}
                    componentProps={{
                      options: [
                        {
                          value: DeductionStatus.Complete,
                          label: "Completed"
                        },
                        {
                          value: DeductionStatus.PartiallyApplied,
                          label: "Partially completed"
                        },
                        {
                          value: DeductionStatus.Pending,
                          label: "Scheduled"
                        }
                      ]
                    }}
                  />

                  <WSFormOld.Field
                    name="collaboratorEmail"
                    label="Contractor"
                    mb="2XL"
                    component={WSSelectOld}
                    componentProps={{
                      searchable: true,
                      placeholder: "All contractors",
                      options: payeeOptions
                    }}
                  />

                  <WSFormOld.Field
                    name="bulkBatchId"
                    label="Bulk Batch"
                    mb="2XL"
                    component={WSSelectOld}
                    componentProps={{
                      searchable: true,
                      placeholder: "All bulk imports",
                      options: filteredBulkBatches.map(bulkBatch => ({
                        label:
                          getUploadedFilename(bulkBatch, BulkResource.Payable) +
                          ` (created ${toWSDateString(bulkBatch.createdAt)})`,
                        value: bulkBatch.bulkPayableBatchId,
                        searchText: [bulkBatch.labels.filename]
                          .filter(Boolean)
                          .join(" ")
                      }))
                    }}
                  />
                </WSFiltersOld>

                {tableData.length > 0 && (
                  <ButtonDownloadCsv
                    mt="M"
                    className={styles.csvLink}
                    getData={async () => {
                      const filter = {
                        memberId: filters.collaboratorEmail
                          ? queryAllPayeeRows.data?.find(
                              c => c.user?.email === filters.collaboratorEmail
                            )?.payeeId
                          : undefined,
                        status: filters.status
                          ? { in: filters.status }
                          : undefined,
                        sort: filters.sort
                      };

                      const deductions = await getAllEntries(
                        paymentsService.collaboratorDeductions.list,
                        filter
                      );

                      return mapDeductionsTableToCSV(
                        deductions.map(deduction => {
                          const payeeRow = queryAllPayeeRows.data.find(
                            row => row.payeeId === deduction.memberId
                          );

                          return {
                            deduction,
                            payeeRow
                          };
                        })
                      );
                    }}
                    fileName="Deductions"
                  />
                )}
              </WSFlexBox>

              <WSInfiniteScroll
                onLoad={() => {
                  qDeductions.fetchNextPage();
                }}
                loadMore={tableData.length > 0}
                endOfList={!qDeductions.hasNextPage}
                loading={!!qDeductions.isFetchingNextPage}
              >
                <WSTable<IDeductionResponse>
                  mt="2XL"
                  tableData={tableData}
                  bottomMessage={
                    !tableData.length && (
                      <WSFlexBox.Center mt="2XL" direction="column">
                        <WSText weight="medium">No deductions found.</WSText>
                        <WSText.ParagraphSm mt="M" color="gray500">
                          To subtract an amount from a contractor's pay,
                          schedule a
                          <WSButton.Link
                            ml="XS"
                            onClick={() => addPayeeDeductionModal.open()}
                          >
                            new deduction.
                          </WSButton.Link>
                        </WSText.ParagraphSm>
                      </WSFlexBox.Center>
                    )
                  }
                  onRowClick={item => {
                    if (item.data.status === DeductionStatus.Complete) {
                      history.push({
                        pathname: `/member/invoices/payables/deductions/completed/${item.data.deductionId}`,
                        search: history.location.search
                      });
                    } else {
                      history.push({
                        pathname: `/member/invoices/payables/deductions/scheduled/${item.data.deductionId}`,
                        search: history.location.search
                      });
                    }
                  }}
                  columns={[
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        hideOnScreens: ["XS", "S"],
                        header: {
                          text: "Contractor"
                        }
                      },
                      renderFunction: ({ data }) => {
                        const payeeRow = queryAllPayeeRows.data?.find(
                          row => row.payeeId === data?.memberId
                        );

                        if (!payeeRow) {
                          return null;
                        }

                        const name = wsName({
                          user: payeeRow.user,
                          member: payeeRow.member,
                          payerOwnedData: payeeRow.payerOwnedData
                        });

                        return (
                          <WSTableCell
                            text={name.getResolvedName()}
                            secondaryText={name.getResolvedSecondaryName()}
                            truncationOptions={{
                              text: { allowTwoLines: true }
                            }}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        sortDirection: mapAndRevertParamsSortToTable(
                          filters.sort?.name
                        ),
                        onColumnSort: onNameSort,
                        header: {
                          text: "Name"
                        }
                      },
                      renderFunction: ({ data }) => {
                        return <WSTableCell text={data.name} />;
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: "Status"
                        }
                      },
                      renderFunction: ({ data }) => {
                        const isComplete =
                          data.status === DeductionStatus.Complete;
                        const isPartiallyApplied =
                          data.status === DeductionStatus.PartiallyApplied;

                        return (
                          <WSTableCell
                            pill={{
                              icon: isComplete
                                ? "check-double"
                                : isPartiallyApplied
                                ? "check"
                                : "time",
                              theme: isComplete
                                ? "success"
                                : isPartiallyApplied
                                ? "blue"
                                : "neutral",
                              text: startCase(data.status)
                            }}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        hideOnScreens: ["XS", "S"],
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: "Date created"
                        }
                      },
                      renderFunction: ({ data }) => (
                        <WSTableCell
                          text={toWSDateString(data.createdAt, "mobileInput")}
                        />
                      )
                    },
                    {
                      config: {
                        hideOnScreens: ["XS", "S"],
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: `Remaining balance ${
                            currencyCode ? `(${currencyCode})` : ""
                          }`
                        }
                      },
                      renderFunction: ({ data }) => {
                        const deductedAmount = data.application
                          .map(a => a.amountDeducted)
                          .reduce((a, b) => a + b, 0);

                        const remainingAmount = deductedAmount
                          ? data.amount - deductedAmount
                          : data.amount;

                        return (
                          <WSTableCell
                            text={formatMoney(remainingAmount, data.currency)}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        justify: "end",
                        sortDirection: mapAndRevertParamsSortToTable(
                          filters.sort?.amount
                        ),
                        onColumnSort: onAmountSort,
                        header: {
                          text: `Total ${
                            currencyCode ? `(${currencyCode})` : ""
                          }`
                        }
                      },
                      renderFunction: ({ data }) => {
                        return (
                          <WSTableCell
                            text={formatMoney(data.amount, data.currency)}
                          />
                        );
                      }
                    }
                  ]}
                />
              </WSInfiniteScroll>
            </>
          );
        }}
      </WSQueries>
    </>
  );
};
